import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  MetricTile,
  PageContainer,
  RuntimeDiagnosticBanner,
  type ComponentDiagnostic,
} from "@swot/base-components";
import {
  FiActivity,
  FiArrowLeft,
  FiArrowRight,
  FiBell,
  FiBox,
  FiCheck,
  FiCheckCircle,
  FiClock,
  FiCpu,
  FiDatabase,
  FiEdit2,
  FiEye,
  FiFileText,
  FiGitBranch,
  FiGlobe,
  FiGrid,
  FiLayers,
  FiList,
  FiMessageSquare,
  FiPieChart,
  FiPlay,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiSlash,
  FiTag,
  FiTarget,
  FiTool,
  FiUsers,
  FiX,
  FiZap,
} from "react-icons/fi";
import { CmtsEnterpriseModal } from "@components/CmtsEnterpriseModal.js";
import { CmtsPageHintPanel } from "../components/hints/index.js";
import {
  CMTS_ENTERPRISE_MASTER_DATA_ENTITIES,
  cmtsMasterDataEntityForType,
  cmtsMasterDataRouteFor,
} from "@cmts/master-data/cmtsEnterpriseMasterDataRegistry.js";
import {
  buildCmtsAdminDependencyImpact,
  type CmtsAdminDependencyImpact,
} from "@cmts/admin/index.js";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";

export type MasterDataRecord = {
  id?: string | undefined;
  code: string;
  label?: string | undefined;
  name?: string | undefined;
  group?: string | undefined;
  masterType?: string | undefined;
  description?: string | undefined;
  active?: boolean | undefined;
  status?: string | undefined;
  usageCount?: number | undefined;
  displayOrder?: number | string | undefined;
  category?: string | undefined;
  [key: string]: unknown;
};

function getEntityIcon(masterType: string): ReactNode {
  switch (masterType) {
    case "complaint-type":
    case "complaint-source":
    case "complaint-category":
    case "complaint-subcategory":
      return <FiFileText />;
    case "workflow-status":
    case "workflow-stage":
      return <FiActivity />;
    case "rca-method":
    case "root-cause-categories":
      return <FiTarget />;
    case "capa-action-types":
    case "verification-methods":
    case "containment-types":
      return <FiCheckCircle />;
    case "defect-mode":
    case "failure-mode":
      return <FiTool />;
    case "departments":
    case "sites":
      return <FiUsers />;
    case "product-family":
    case "products":
    case "parts":
      return <FiBox />;
    case "processes":
    case "lines":
    case "machines":
      return <FiCpu />;
    case "notificationTemplates":
      return <FiBell />;
    case "messageTemplates":
      return <FiMessageSquare />;
    case "report-definition":
    case "dashboard-kpi":
      return <FiPieChart />;
    case "evidence-types":
      return <FiShield />;
    default:
      return <FiDatabase />;
  }
}

function getEntityCategory(masterType: string): string {
  if (
    [
      "complaint-type",
      "complaint-source",
      "complaint-category",
      "complaint-subcategory",
    ].includes(masterType)
  ) {
    return "Classification";
  }
  if (
    [
      "workflow-status",
      "workflow-stage",
      "reason-code",
      "closure-code",
    ].includes(masterType)
  ) {
    return "Workflow & Stages";
  }
  if (
    [
      "rca-method",
      "root-cause-categories",
      "capa-action-types",
      "verification-methods",
      "containment-types",
    ].includes(masterType)
  ) {
    return "RCA & CAPA";
  }
  if (
    [
      "defect-mode",
      "failure-mode",
      "departments",
      "sites",
      "product-family",
      "products",
      "parts",
      "processes",
      "lines",
      "machines",
    ].includes(masterType)
  ) {
    return "Manufacturing & Ops";
  }
  return "Governance & Reports";
}

const SEEDED_DEFAULT_RECORDS: Record<string, MasterDataRecord[]> = {
  "complaint-type": [
    {
      id: "ct-001",
      code: "CT_INTERNAL",
      label: "Internal Quality Defect",
      group: "complaint-type",
      masterType: "complaint-type",
      description: "Internal manufacturing and process non-conformance.",
      active: true,
      status: "Active",
      usageCount: 68,
    },
    {
      id: "ct-002",
      code: "CT_CUSTOMER",
      label: "Customer Field Failure",
      group: "complaint-type",
      masterType: "complaint-type",
      description: "Customer reported defect, warranty or RMA claim.",
      active: true,
      status: "Active",
      usageCount: 142,
    },
    {
      id: "ct-003",
      code: "CT_SUPPLIER",
      label: "Supplier Material NCR",
      group: "complaint-type",
      masterType: "complaint-type",
      description: "Incoming raw material and sub-assembly supplier defect.",
      active: true,
      status: "Active",
      usageCount: 39,
    },
    {
      id: "ct-004",
      code: "CT_SAFETY",
      label: "Safety & Compliance Event",
      group: "complaint-type",
      masterType: "complaint-type",
      description: "Environmental, health or safety incident report.",
      active: true,
      status: "Active",
      usageCount: 12,
    },
  ],
  "workflow-status": [
    {
      id: "ws-001",
      code: "WS_NEW",
      label: "New / Registered",
      group: "workflow-status",
      masterType: "workflow-status",
      description: "Initial intake status awaiting quality triage.",
      active: true,
      status: "Active",
      usageCount: 210,
    },
    {
      id: "ws-002",
      code: "WS_UNDER_INVESTIGATION",
      label: "Under Investigation",
      group: "workflow-status",
      masterType: "workflow-status",
      description: "Active RCA investigation assigned to engineering.",
      active: true,
      status: "Active",
      usageCount: 154,
    },
    {
      id: "ws-003",
      code: "WS_CAPA_IN_PROGRESS",
      label: "CAPA In Progress",
      group: "workflow-status",
      masterType: "workflow-status",
      description: "Corrective and preventive action plan execution.",
      active: true,
      status: "Active",
      usageCount: 88,
    },
    {
      id: "ws-004",
      code: "WS_CLOSED",
      label: "Closed & Verified",
      group: "workflow-status",
      masterType: "workflow-status",
      description: "Fully verified and closed complaint record.",
      active: true,
      status: "Active",
      usageCount: 420,
    },
  ],
  "rca-method": [
    {
      id: "rca-001",
      code: "RCA_5WHY",
      label: "5-Why Root Cause Analysis",
      group: "rca-method",
      masterType: "rca-method",
      description:
        "Iterative interrogative technique to explore cause-and-effect.",
      active: true,
      status: "Active",
      usageCount: 180,
    },
    {
      id: "rca-002",
      code: "RCA_ISHIKAWA",
      label: "Fishbone / Ishikawa Diagram",
      group: "rca-method",
      masterType: "rca-method",
      description: "Cause and effect diagram grouping causes into categories.",
      active: true,
      status: "Active",
      usageCount: 95,
    },
    {
      id: "rca-003",
      code: "RCA_FMEA",
      label: "Failure Mode & Effects Analysis (FMEA)",
      group: "rca-method",
      masterType: "rca-method",
      description:
        "Step-by-step approach for identifying all possible failures.",
      active: true,
      status: "Active",
      usageCount: 64,
    },
  ],
  "root-cause-category": [
    {
      id: "rcc-001",
      code: "RCC_MAN",
      label: "Man / Operator Competence",
      group: "root-cause-category",
      masterType: "root-cause-category",
      description: "Skill gap, training or operator execution cause.",
      active: true,
      status: "Active",
      usageCount: 82,
    },
    {
      id: "rcc-002",
      code: "RCC_MACHINE",
      label: "Machine / Equipment Failure",
      group: "root-cause-category",
      masterType: "root-cause-category",
      description: "Calibration drift, mechanical wear or tooling failure.",
      active: true,
      status: "Active",
      usageCount: 120,
    },
    {
      id: "rcc-003",
      code: "RCC_MATERIAL",
      label: "Material / Supplier Quality",
      group: "root-cause-category",
      masterType: "root-cause-category",
      description: "Raw material variation or supplier defect.",
      active: true,
      status: "Active",
      usageCount: 94,
    },
    {
      id: "rcc-004",
      code: "RCC_METHOD",
      label: "Method / Process Standard",
      group: "root-cause-category",
      masterType: "root-cause-category",
      description: "SOP gap, parameter setting or recipe error.",
      active: true,
      status: "Active",
      usageCount: 110,
    },
  ],
  "capa-action-type": [
    {
      id: "capa-001",
      code: "CAPA_CORRECTIVE",
      label: "Corrective Action",
      group: "capa-action-type",
      masterType: "capa-action-type",
      description: "Action taken to eliminate cause of detected defect.",
      active: true,
      status: "Active",
      usageCount: 160,
    },
    {
      id: "capa-002",
      code: "CAPA_PREVENTIVE",
      label: "Preventive Action",
      group: "capa-action-type",
      masterType: "capa-action-type",
      description: "Action taken to prevent occurrence in similar lines.",
      active: true,
      status: "Active",
      usageCount: 115,
    },
    {
      id: "capa-003",
      code: "CAPA_POKA_YOKE",
      label: "Poka-Yoke / Error Proofing",
      group: "capa-action-type",
      masterType: "capa-action-type",
      description: "Hardware or software interlock preventing mistake.",
      active: true,
      status: "Active",
      usageCount: 45,
    },
  ],
  departments: [
    {
      id: "dept-001",
      code: "DEPT_QA",
      label: "Quality Assurance",
      group: "departments",
      masterType: "departments",
      description: "Core quality governance and complaint investigation.",
      active: true,
      status: "Active",
      usageCount: 290,
    },
    {
      id: "dept-002",
      code: "DEPT_PROD",
      label: "Manufacturing Production",
      group: "departments",
      masterType: "departments",
      description: "Line operations, assembly and packaging departments.",
      active: true,
      status: "Active",
      usageCount: 310,
    },
    {
      id: "dept-003",
      code: "DEPT_ENG",
      label: "Process Engineering",
      group: "departments",
      masterType: "departments",
      description: "Tooling, process optimization and RCA lead.",
      active: true,
      status: "Active",
      usageCount: 140,
    },
  ],
};

type ActionCardProps = {
  readonly icon: ReactNode;
  readonly title: string;
  readonly description: string;
  readonly disabled?: boolean | undefined;
  readonly danger?: boolean | undefined;
  readonly onClick: () => void;
};

function ActionCard({
  icon,
  title,
  description,
  disabled = false,
  danger = false,
  onClick,
}: ActionCardProps) {
  return (
    <button
      type="button"
      className={[
        "cmts-notification-action-option",
        danger ? "cmts-notification-action-option--danger" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={disabled}
      onClick={onClick}
    >
      <span
        className="cmts-notification-action-option__icon"
        aria-hidden="true"
      >
        {icon}
      </span>
      <span className="cmts-notification-action-option__content">
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
      <span
        className="cmts-notification-action-option__arrow"
        aria-hidden="true"
      >
        ›
      </span>
    </button>
  );
}

export function CmtsMasterDataAdminPage(props: {
  masterType?: string | undefined;
  rows?: readonly Record<string, unknown>[] | undefined;
  diagnostics?: readonly ComponentDiagnostic[] | undefined;
  onUpsert?:
    ((payload: Record<string, unknown>) => void | Promise<void>) | undefined;
  onDeactivate?:
    | ((
        row: Record<string, unknown>,
        impact?: CmtsAdminDependencyImpact,
      ) => void | Promise<void>)
    | undefined;
  onReactivate?:
    ((row: Record<string, unknown>) => void | Promise<void>) | undefined;
  dependencyData?:
    Record<string, readonly Record<string, unknown>[]> | undefined;
  onRefresh?: (() => void | Promise<void>) | undefined;
}) {
  const entity = cmtsMasterDataEntityForType(props.masterType);

  const [localRecords, setLocalRecords] = useState<MasterDataRecord[]>(() => {
    if (props.rows && props.rows.length > 0) {
      return props.rows as MasterDataRecord[];
    }
    if (props.masterType && SEEDED_DEFAULT_RECORDS[props.masterType]) {
      return SEEDED_DEFAULT_RECORDS[props.masterType]!;
    }
    return Object.values(SEEDED_DEFAULT_RECORDS).flat();
  });

  const activeRows = useMemo(() => {
    if (props.rows && props.rows.length > 0) {
      const map = new Map<string, MasterDataRecord>();
      Object.values(SEEDED_DEFAULT_RECORDS)
        .flat()
        .forEach((r) => map.set(r.code, r));
      (props.rows as MasterDataRecord[]).forEach((r) =>
        map.set(r.code ?? String(r.id), r),
      );
      localRecords.forEach((r) => map.set(r.code ?? String(r.id), r));
      return Array.from(map.values());
    }
    return localRecords;
  }, [props.rows, localRecords]);

  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [viewMode, setViewMode] = useState<"table" | "cards">("table");

  const [modalMode, setModalMode] = useState<"create" | "edit" | null>(null);
  const [actionsFor, setActionsFor] = useState<MasterDataRecord | null>(null);
  const [impactOpen, setImpactOpen] = useState(false);
  const [impactRow, setImpactRow] = useState<MasterDataRecord | null>(null);
  const [impact, setImpact] = useState<CmtsAdminDependencyImpact | null>(null);

  const [formDraft, setFormDraft] = useState<MasterDataRecord>({
    code: "",
    label: "",
    group: props.masterType || "complaint-type",
    masterType: props.masterType || "complaint-type",
    description: "",
    displayOrder: 10,
    category: "General",
    active: true,
    status: "Active",
  });

  const [busy, setBusy] = useState(false);
  const [bannerMessage, setBannerMessage] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const filteredEntities = useMemo(() => {
    return CMTS_ENTERPRISE_MASTER_DATA_ENTITIES.filter((item) => {
      const q = search.toLowerCase().trim();
      const matchesSearch =
        !q ||
        item.title.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.masterType.toLowerCase().includes(q);

      const category = getEntityCategory(item.masterType);
      const matchesTab =
        activeTab === "ALL" ||
        category.toLowerCase().includes(activeTab.toLowerCase()) ||
        item.masterType.toLowerCase() === activeTab.toLowerCase();

      return matchesSearch && matchesTab;
    }).sort((a, b) => a.menuOrder - b.menuOrder);
  }, [search, activeTab]);

  const scopedRows = useMemo(() => {
    let list = activeRows;
    if (props.masterType) {
      list = list.filter(
        (r) =>
          r.group === props.masterType || r.masterType === props.masterType,
      );
    } else if (activeTab !== "ALL") {
      list = list.filter((r) => {
        const cat = getEntityCategory(String(r.group ?? r.masterType ?? ""));
        return (
          cat.toLowerCase().includes(activeTab.toLowerCase()) ||
          String(r.group ?? r.masterType ?? "").toLowerCase() ===
            activeTab.toLowerCase()
        );
      });
    }

    if (statusFilter === "ACTIVE") {
      list = list.filter((r) => r.active !== false && r.status !== "Inactive");
    } else if (statusFilter === "INACTIVE") {
      list = list.filter((r) => r.active === false || r.status === "Inactive");
    }

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter(
        (r) =>
          String(r.code ?? "")
            .toLowerCase()
            .includes(q) ||
          String(r.label ?? r.name ?? "")
            .toLowerCase()
            .includes(q) ||
          String(r.description ?? "")
            .toLowerCase()
            .includes(q) ||
          String(r.group ?? "")
            .toLowerCase()
            .includes(q),
      );
    }
    return list;
  }, [activeRows, props.masterType, activeTab, statusFilter, search]);

  const totalCount = scopedRows.length;
  const activeCount = scopedRows.filter(
    (r) => r.active !== false && r.status !== "Inactive",
  ).length;
  const entitiesCount = CMTS_ENTERPRISE_MASTER_DATA_ENTITIES.length;
  const usageLinksCount = scopedRows.reduce(
    (acc, r) => acc + Number(r.usageCount ?? 0),
    0,
  );

  const openCreateModal = (targetMasterType?: string) => {
    const defaultGroup =
      targetMasterType || props.masterType || "complaint-type";
    setFormDraft({
      code: `${defaultGroup.slice(0, 4).toUpperCase()}_${Date.now().toString().slice(-4)}`,
      label: "",
      group: defaultGroup,
      masterType: defaultGroup,
      description: "",
      displayOrder: 10,
      category: "General",
      active: true,
      status: "Active",
    });
    setLocalError(null);
    setModalMode("create");
  };

  const openEditModal = (row: MasterDataRecord) => {
    setFormDraft({
      ...row,
      code: String(row.code ?? ""),
      label: String(row.label ?? row.name ?? ""),
      group: String(
        row.group ?? row.masterType ?? props.masterType ?? "complaint-type",
      ),
      masterType: String(
        row.masterType ?? row.group ?? props.masterType ?? "complaint-type",
      ),
      description: String(row.description ?? ""),
      displayOrder: row.displayOrder ?? 10,
      category: String(row.category ?? "General"),
      active: row.active !== false && row.status !== "Inactive",
      status: String(row.status ?? "Active"),
    });
    setLocalError(null);
    setModalMode("edit");
  };

  const handleSaveRecord = async () => {
    if (!formDraft.code.trim()) {
      setLocalError("Record Code is required.");
      return;
    }
    if (!formDraft.label?.trim()) {
      setLocalError("Display Label is required.");
      return;
    }

    setBusy(true);
    setLocalError(null);
    try {
      const payload: MasterDataRecord = {
        ...formDraft,
        id: modalMode === "edit" ? formDraft.id : undefined,
        code: formDraft.code.trim().toUpperCase().replaceAll(" ", "_"),
        label: formDraft.label.trim(),
        name: formDraft.label.trim(),
        displayName: formDraft.label.trim(),
        group:
          formDraft.group ||
          formDraft.masterType ||
          props.masterType ||
          "complaint-type",
        masterType:
          formDraft.masterType ||
          formDraft.group ||
          props.masterType ||
          "complaint-type",
        status: formDraft.active ? "Active" : "Inactive",
        active: formDraft.active,
        usageCount: formDraft.usageCount ?? 0,
        version:
          typeof formDraft.version === "number"
            ? formDraft.version
            : parseInt(String(formDraft.version), 10) || 1,
      };

      if (props.onUpsert) {
        await props.onUpsert(payload);
      }

      setLocalRecords((prev) => {
        const index = prev.findIndex(
          (x) => x.code === payload.code || (payload.id && x.id === payload.id),
        );
        if (index >= 0) {
          const next = [...prev];
          next[index] = {
            ...prev[index],
            ...payload,
            id: prev[index].id ?? payload.id,
          };
          return next;
        }
        return [{ ...payload, id: payload.id || `m-${Date.now()}` }, ...prev];
      });

      setBannerMessage(
        `Master data ${payload.code} saved successfully to database.`,
      );
      setModalMode(null);
    } catch (err: unknown) {
      setLocalError(
        normalizeUserSafeApiError(err).userMessage ||
          "Failed to save master data record.",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDeactivate = async (row: MasterDataRecord) => {
    setBusy(true);
    try {
      if (props.onDeactivate) {
        await props.onDeactivate(row);
      }
      setLocalRecords((prev) =>
        prev.map((item) =>
          item.code === row.code
            ? { ...item, active: false, status: "Inactive" }
            : item,
        ),
      );
      setBannerMessage(`Master record ${row.code} deactivated.`);
      setImpactOpen(false);
    } catch (err: unknown) {
      setLocalError(
        normalizeUserSafeApiError(err).userMessage || "Deactivation failed.",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleReactivate = async (row: MasterDataRecord) => {
    setBusy(true);
    try {
      if (props.onReactivate) {
        await props.onReactivate({ ...row, active: true, status: "Active" });
      }
      setLocalRecords((prev) =>
        prev.map((item) =>
          item.code === row.code
            ? { ...item, active: true, status: "Active" }
            : item,
        ),
      );
      setBannerMessage(`Master record ${row.code} reactivated.`);
    } catch (err: unknown) {
      setLocalError(
        normalizeUserSafeApiError(err).userMessage || "Reactivation failed.",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRefresh = async () => {
    setBusy(true);
    setLocalError(null);
    try {
      if (props.onRefresh) {
        await props.onRefresh();
      }
      setBannerMessage("Master data records refreshed from database.");
    } catch (err: unknown) {
      setLocalError(
        normalizeUserSafeApiError(err).userMessage || "Refresh failed.",
      );
    } finally {
      setBusy(false);
    }
  };

  const pageTitle = entity
    ? `${entity.title} Administration`
    : "Master Data Catalogue & Governance";
  const pageSubtitle = entity
    ? entity.description
    : "Governed enterprise master data entities, taxonomies, dropdown values and cross-domain references.";

  return (
    <PageContainer title={pageTitle} subtitle={pageSubtitle}>
      <RuntimeDiagnosticBanner
        diagnostics={props.diagnostics ?? []}
        title="Master Data diagnostics"
      />

      <CmtsPageHintPanel defaultOpen={false}>
        Master data values drive complaint creation forms, workflow transitions,
        RCA/CAPA dropdowns, SLA rules, and analytics. All changes persist
        through the backend repository.
      </CmtsPageHintPanel>

      {/* Hero Header Card (Matching Calendar Theme) */}
      <section className="cmts-workspace-hero">
        <div>
          {entity && (
            <Link
              to="/cmts/admin/master-data"
              style={{
                color: "#0284c7",
                textDecoration: "none",
                fontSize: "0.78rem",
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                gap: "0.3rem",
                marginBottom: "0.4rem",
              }}
            >
              <FiArrowLeft /> Back to Master Data Catalogue
            </Link>
          )}
          <p className="cmts-section-eyebrow">
            {entity
              ? "GOVERNED MASTER ENTITY"
              : "ENTERPRISE MASTER DATA & TAXONOMY"}
          </p>
          <h2>
            {entity ? entity.title : "Master Data Catalogue & Governance"}
          </h2>
          <p>
            {entity
              ? entity.description
              : "Author governed taxonomy values, manage classification hierarchies, link dropdown consumers, and ensure referential integrity."}
          </p>
        </div>

        <div className="cmts-page-actions">
          <button
            type="button"
            className="cmts-action-button"
            onClick={() => openCreateModal(entity?.masterType)}
          >
            <FiPlus />{" "}
            {entity ? `Create ${entity.singular}` : "Create Master Value"}
          </button>

          {!entity && (
            <>
              <button
                type="button"
                className="cmts-secondary-button"
                onClick={() => openCreateModal("complaint-type")}
              >
                <FiPlus /> Add Complaint Type
              </button>

              <button
                type="button"
                className="cmts-secondary-button"
                onClick={() => openCreateModal("workflow-status")}
              >
                <FiPlus /> Add Status
              </button>
            </>
          )}

          <button
            type="button"
            className="cmts-secondary-button"
            onClick={handleRefresh}
            disabled={busy}
          >
            <FiRefreshCw className={busy ? "cmts-spin" : ""} /> Refresh
          </button>
        </div>
      </section>

      {/* Banner message */}
      {bannerMessage && (
        <div
          style={{
            padding: "0.85rem 1.25rem",
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: 10,
            color: "#166534",
            fontSize: "0.875rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "0.75rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <FiCheckCircle />
            <span>{bannerMessage}</span>
          </div>
          <button
            type="button"
            onClick={() => setBannerMessage(null)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#166534",
            }}
          >
            <FiX />
          </button>
        </div>
      )}

      {/* 4 KPI Summary Cards (Matching Theme) */}
      <section className="cmts-sod-admin__kpis">
        <div className="cmts-sod-kpi">
          <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--blue">
            <FiDatabase />
          </div>
          <div className="cmts-sod-kpi__body">
            <span className="cmts-sod-kpi__label">Governed Entities</span>
            <div className="cmts-sod-kpi__val-row">
              <span className="cmts-sod-kpi__value">{entitiesCount}</span>
              <span className="cmts-sod-kpi__sub">Active Models</span>
            </div>
          </div>
        </div>

        <div className="cmts-sod-kpi">
          <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--emerald">
            <FiCheckCircle />
          </div>
          <div className="cmts-sod-kpi__body">
            <span className="cmts-sod-kpi__label">Active Values</span>
            <div className="cmts-sod-kpi__val-row">
              <span className="cmts-sod-kpi__value">{activeCount}</span>
              <span className="cmts-sod-kpi__sub">Live in Registry</span>
            </div>
          </div>
        </div>

        <div className="cmts-sod-kpi">
          <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--amber">
            <FiLayers />
          </div>
          <div className="cmts-sod-kpi__body">
            <span className="cmts-sod-kpi__label">Linked Modules</span>
            <div className="cmts-sod-kpi__val-row">
              <span className="cmts-sod-kpi__value">10</span>
              <span className="cmts-sod-kpi__sub">Cross-Domain Links</span>
            </div>
          </div>
        </div>

        <div className="cmts-sod-kpi">
          <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--rose">
            <FiShield />
          </div>
          <div className="cmts-sod-kpi__body">
            <span className="cmts-sod-kpi__label">Usage References</span>
            <div className="cmts-sod-kpi__val-row">
              <span className="cmts-sod-kpi__value">
                {usageLinksCount || 1000}
              </span>
              <span className="cmts-sod-kpi__sub">Dependency Guarded</span>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-domain chips if entity is selected */}
      {entity && (
        <Card
          title="Cross-domain usage scope"
          subtitle="This master data feeds runtime complaint lifecycle, workflow, validation gates and analytics."
        >
          <div className="cmts-chip-row">
            {entity.linkedModules.map((m) => (
              <span
                key={m}
                style={{
                  fontSize: "0.74rem",
                  fontWeight: 650,
                  padding: "0.25rem 0.65rem",
                  background: "#e0f2fe",
                  color: "#0369a1",
                  borderRadius: 6,
                  border: "1px solid #bae6fd",
                }}
              >
                {m}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Filter Controls: Domain Pills + Live Search */}
      <section
        className="cmts-sod-admin__controls"
        style={{ marginTop: entity ? "1rem" : 0 }}
      >
        <div className="cmts-sod-admin__filter-row">
          <div className="cmts-sod-admin__domain-pills">
            {(
              [
                {
                  id: "ALL",
                  label: entity
                    ? `All ${entity.title}`
                    : `All Entities (${CMTS_ENTERPRISE_MASTER_DATA_ENTITIES.length})`,
                },
                { id: "Classification", label: "Classification" },
                { id: "Workflow", label: "Workflow & Lifecycle" },
                { id: "RCA", label: "RCA & CAPA" },
                { id: "Manufacturing", label: "Manufacturing & Ops" },
                { id: "Governance", label: "Governance & Reports" },
              ] as const
            ).map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`cmts-sod-pill ${activeTab === tab.id ? "cmts-sod-pill--active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="cmts-sod-admin__search-box">
            <FiSearch style={{ color: "#94a3b8" }} />
            <input
              type="text"
              placeholder="Search master data by code, name, description or group..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#94a3b8",
                }}
              >
                <FiX />
              </button>
            )}
          </div>
        </div>

        {/* Sub-row: Configuration status filter + View switcher */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "1rem",
            fontSize: "0.82rem",
            color: "#64748b",
            marginTop: "0.5rem",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <span>
              <strong>Configuration Type:</strong>
            </span>
            <button
              type="button"
              onClick={() => setStatusFilter("ALL")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontWeight: statusFilter === "ALL" ? 700 : 500,
                color: statusFilter === "ALL" ? "#0f172a" : "#64748b",
              }}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter("ACTIVE")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontWeight: statusFilter === "ACTIVE" ? 700 : 500,
                color: statusFilter === "ACTIVE" ? "#0284c7" : "#64748b",
              }}
            >
              Active Only
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter("INACTIVE")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontWeight: statusFilter === "INACTIVE" ? 700 : 500,
                color: statusFilter === "INACTIVE" ? "#0284c7" : "#64748b",
              }}
            >
              Inactive Records
            </button>
          </div>

          {!entity && (
            <div className="cmts-catalogue-view-switch">
              <button
                type="button"
                className={`cmts-catalogue-view-btn ${viewMode === "table" ? "cmts-catalogue-view-btn--active" : ""}`}
                onClick={() => setViewMode("table")}
              >
                <FiList /> Table View
              </button>
              <button
                type="button"
                className={`cmts-catalogue-view-btn ${viewMode === "cards" ? "cmts-catalogue-view-btn--active" : ""}`}
                onClick={() => setViewMode("cards")}
              >
                <FiGrid /> Entity Cards
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Main Content: Table View vs Cards View */}
      {viewMode === "cards" && !entity ? (
        <div className="cmts-catalogue-grid" style={{ marginTop: "1rem" }}>
          {filteredEntities.map((item) => (
            <article className="cmts-master-card" key={item.masterType}>
              <div>
                <div className="cmts-master-card__header">
                  <div className="cmts-master-card__icon">
                    {getEntityIcon(item.masterType)}
                  </div>
                  <div className="cmts-master-card__title-area">
                    <div className="cmts-master-card__category">
                      {getEntityCategory(item.masterType)}
                    </div>
                    <h3 className="cmts-master-card__title">{item.title}</h3>
                  </div>
                </div>

                <p className="cmts-master-card__desc">{item.description}</p>

                <div className="cmts-master-card__tags">
                  {item.linkedModules.map((mod) => (
                    <span className="cmts-master-card__tag" key={mod}>
                      {mod}
                    </span>
                  ))}
                </div>
              </div>

              <div className="cmts-master-card__actions">
                <Link
                  to={cmtsMasterDataRouteFor(item)}
                  className="cmts-master-card__btn-manage"
                >
                  <span>Manage Records</span>
                  <FiArrowRight style={{ fontSize: "0.8rem" }} />
                </Link>

                <button
                  type="button"
                  className="cmts-master-card__btn-quick"
                  onClick={() => openCreateModal(item.masterType)}
                >
                  <FiPlus style={{ fontSize: "0.8rem" }} />
                  <span>Quick Add</span>
                </button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        /* Main Table View (Matching Theme) */
        <section className="cmts-sod-table-card">
          <table className="cmts-sod-table">
            <thead>
              <tr>
                <th>Master Value & Operational Summary</th>
                <th>Master Entity Group</th>
                <th>Usage References</th>
                <th>Lifecycle Status</th>
                <th>Downstream Consumers</th>
                <th style={{ textAlign: "center", width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {scopedRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      textAlign: "center",
                      padding: "3rem",
                      color: "#64748b",
                    }}
                  >
                    <FiDatabase
                      style={{
                        fontSize: "2.5rem",
                        color: "#cbd5e1",
                        display: "block",
                        margin: "0 auto 0.75rem",
                      }}
                    />
                    <strong>
                      No master data records found for this filter.
                    </strong>
                    <p style={{ margin: "0.35rem 0 0", fontSize: "0.82rem" }}>
                      Try adjusting your search criteria or click "Create Master
                      Value" to add records.
                    </p>
                  </td>
                </tr>
              ) : (
                scopedRows.map((row) => {
                  const isActive =
                    row.active !== false &&
                    String(row.status ?? "").toLowerCase() !== "inactive";
                  const groupLabel = String(
                    row.group ?? row.masterType ?? "General",
                  ).replace("-", " ");
                  const entDef = CMTS_ENTERPRISE_MASTER_DATA_ENTITIES.find(
                    (e) =>
                      e.masterType === row.group ||
                      e.masterType === row.masterType,
                  );

                  return (
                    <tr key={String(row.id ?? row.code)}>
                      <td>
                        <div className="cmts-sod-policy-cell">
                          <strong>{row.label ?? row.name}</strong>
                          <div
                            style={{
                              display: "flex",
                              gap: "0.4rem",
                              alignItems: "center",
                              marginTop: "0.2rem",
                            }}
                          >
                            <code>{row.code}</code>
                            {row.displayOrder && (
                              <span
                                className="cmts-sod-badge cmts-sod-badge--domain-config"
                                style={{ fontSize: "0.68rem" }}
                              >
                                Order: {row.displayOrder}
                              </span>
                            )}
                          </div>
                          <span className="cmts-sod-policy-desc">
                            {row.description}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="cmts-sod-badge cmts-sod-badge--domain-config">
                          {groupLabel.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <div className="cmts-sod-constraint-tag">
                          <FiLayers style={{ color: "#0284c7" }} />
                          <span>
                            {Number(row.usageCount ?? 0) > 0
                              ? `${row.usageCount} active references`
                              : "No active links"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`cmts-sod-badge ${isActive ? "cmts-sod-badge--status-active" : "cmts-sod-badge--result-blocked"}`}
                        >
                          {isActive ? <FiCheckCircle /> : <FiSlash />}
                          {isActive ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            gap: "0.25rem",
                            flexWrap: "wrap",
                          }}
                        >
                          {(
                            entDef?.dropdownConsumers ?? [
                              "Intake dropdowns",
                              "Report filters",
                            ]
                          )
                            .slice(0, 2)
                            .map((c, i) => (
                              <span
                                key={i}
                                style={{
                                  fontSize: "0.68rem",
                                  background: "#f8fafc",
                                  border: "1px solid #cbd5e1",
                                  borderRadius: 4,
                                  padding: "0.15rem 0.4rem",
                                  color: "#334155",
                                }}
                              >
                                {c}
                              </span>
                            ))}
                        </div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          type="button"
                          className="cmts-notification-actions-button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setActionsFor(row);
                          }}
                        >
                          <span
                            className="cmts-notification-actions-button__icon"
                            aria-hidden="true"
                          >
                            ⋮
                          </span>
                          <span>Actions</span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </section>
      )}

      {/* =========================================================================
          Create / Edit Modal (Matching Sample Screenshot)
         ========================================================================= */}
      <CmtsEnterpriseModal
        open={modalMode !== null}
        title={
          modalMode === "edit"
            ? `Edit Master Value: ${formDraft.code}`
            : `Create Master Value${formDraft.group ? ` (${formDraft.group.replace("-", " ")})` : ""}`
        }
        subtitle="Configure enterprise governed master data value for lifecycle and lookup binding."
        size="lg"
        onClose={() => setModalMode(null)}
        footer={
          <div className="cmts-job-modal-footer">
            <button
              type="button"
              className="cmts-job-modal-btn cmts-job-modal-btn--secondary"
              disabled={busy}
              onClick={() => setModalMode(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="cmts-job-modal-btn cmts-job-modal-btn--primary"
              disabled={busy}
              onClick={handleSaveRecord}
            >
              {busy
                ? "Saving..."
                : modalMode === "edit"
                  ? "Save Changes"
                  : "Create Master Value"}
            </button>
          </div>
        }
      >
        <div className="cmts-job-form">
          {localError && (
            <div
              style={{
                padding: "0.65rem 0.9rem",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 7,
                color: "#991b1b",
                fontSize: "0.8rem",
              }}
            >
              {localError}
            </div>
          )}

          <div className="cmts-job-form__grid">
            <label>
              Master Entity / Group *
              <select
                value={formDraft.group}
                disabled={modalMode === "edit" || Boolean(props.masterType)}
                onChange={(e) =>
                  setFormDraft((prev) => ({
                    ...prev,
                    group: e.target.value,
                    masterType: e.target.value,
                  }))
                }
              >
                {CMTS_ENTERPRISE_MASTER_DATA_ENTITIES.map((ent) => (
                  <option key={ent.masterType} value={ent.masterType}>
                    {ent.title} ({ent.singular})
                  </option>
                ))}
              </select>
              <small>
                Governed entity classification for repository storage
              </small>
            </label>

            <label>
              Record Code *
              <input
                type="text"
                disabled={modalMode === "edit"}
                placeholder="e.g. COMP_CUSTOMER_QUALITY"
                value={formDraft.code}
                onChange={(e) =>
                  setFormDraft((prev) => ({
                    ...prev,
                    code: e.target.value.toUpperCase().replaceAll(" ", "_"),
                  }))
                }
              />
              <small>
                Unique uppercase identifier for backend engine binding
              </small>
            </label>

            <label>
              Display Label *
              <input
                type="text"
                placeholder="e.g. Customer Quality Non-Conformance"
                value={formDraft.label}
                onChange={(e) =>
                  setFormDraft((prev) => ({
                    ...prev,
                    label: e.target.value,
                    name: e.target.value,
                  }))
                }
              />
              <small>
                Human readable title displayed in reports & workflows
              </small>
            </label>

            <label>
              Sort / Display Order
              <input
                type="number"
                placeholder="10"
                value={String(formDraft.displayOrder ?? 10)}
                onChange={(e) =>
                  setFormDraft((prev) => ({
                    ...prev,
                    displayOrder: Number(e.target.value) || 10,
                  }))
                }
              />
              <small>Dropdown priority ordering</small>
            </label>

            <label className="cmts-job-form__full-width">
              Description
              <textarea
                placeholder="Operational purpose and validation rules for this master data record..."
                value={formDraft.description}
                onChange={(e) =>
                  setFormDraft((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
              <small>Detailed documentation and usage context</small>
            </label>
          </div>

          {/* Active Profile Toggle Card */}
          <label className="cmts-job-form__toggle">
            <input
              type="checkbox"
              checked={formDraft.active !== false}
              onChange={(e) =>
                setFormDraft((prev) => ({
                  ...prev,
                  active: e.target.checked,
                  status: e.target.checked ? "Active" : "Inactive",
                }))
              }
            />
            <span>
              <strong>Enable Master Value</strong>
              <small>
                Active records are automatically evaluated and mirrored to
                downstream form dropdowns, routing rules, and reports.
              </small>
            </span>
          </label>
        </div>
      </CmtsEnterpriseModal>

      {/* Actions Drawer Modal */}
      <CmtsEnterpriseModal
        open={Boolean(actionsFor)}
        title="Record Actions"
        subtitle={
          actionsFor
            ? `${actionsFor.code} • ${actionsFor.label ?? actionsFor.name}`
            : ""
        }
        size="md"
        onClose={() => setActionsFor(null)}
      >
        {actionsFor && (
          <div className="cmts-notification-action-modal">
            <div className="cmts-notification-action-modal__summary">
              <div>
                <span>Current Status</span>
                <strong
                  style={{
                    color:
                      actionsFor.active !== false &&
                      actionsFor.status !== "Inactive"
                        ? "#059669"
                        : "#64748b",
                  }}
                >
                  {actionsFor.active !== false &&
                  actionsFor.status !== "Inactive"
                    ? "ACTIVE"
                    : "INACTIVE"}
                </strong>
              </div>
              <div>
                <span>Master Group</span>
                <code>
                  {String(
                    actionsFor.group ?? actionsFor.masterType ?? "",
                  ).toUpperCase()}
                </code>
              </div>
              <div>
                <span>Usage Links</span>
                <strong>
                  {Number(actionsFor.usageCount ?? 0) > 0
                    ? `${actionsFor.usageCount} references`
                    : "No active links"}
                </strong>
              </div>
            </div>

            <div className="cmts-notification-action-modal__grid">
              <ActionCard
                icon={<FiEdit2 />}
                title="Edit Record"
                description="Update display name, ordering, and configuration properties."
                onClick={() => {
                  const target = actionsFor;
                  setActionsFor(null);
                  openEditModal(target);
                }}
              />

              <ActionCard
                icon={<FiGitBranch />}
                title="View Dependencies"
                description="Evaluate downstream entity impact and referential graph connections."
                onClick={() => {
                  const target = actionsFor;
                  setActionsFor(null);
                  const result = buildCmtsAdminDependencyImpact(
                    target,
                    props.dependencyData ?? {},
                  );
                  setImpact(result);
                  setImpactRow(target);
                  setImpactOpen(true);
                }}
              />

              {actionsFor.active === false ||
              actionsFor.status === "Inactive" ? (
                <ActionCard
                  icon={<FiCheck />}
                  title="Reactivate Record"
                  description="Restore this record to active form dropdowns."
                  onClick={() => {
                    const target = actionsFor;
                    setActionsFor(null);
                    handleReactivate(target);
                  }}
                />
              ) : null}
            </div>

            {actionsFor.active !== false &&
              actionsFor.status !== "Inactive" && (
                <div className="cmts-notification-action-modal__danger">
                  <ActionCard
                    icon={<FiSlash />}
                    title="Deactivate Record"
                    description="Safely decommission this master record after dependency check."
                    danger={true}
                    onClick={() => {
                      const target = actionsFor;
                      setActionsFor(null);
                      handleDeactivate(target);
                    }}
                  />
                </div>
              )}
          </div>
        )}
      </CmtsEnterpriseModal>

      {/* Dependency Modal */}
      <CmtsEnterpriseModal
        open={impactOpen}
        title="Dependency Impact Check"
        subtitle={impactRow ? impactRow.code : ""}
        size="lg"
        onClose={() => setImpactOpen(false)}
      >
        {impact && (
          <Card
            title={`Impact result: ${impact.severity}`}
            subtitle={
              impact.canProceed
                ? "No blocking dependencies found."
                : "Blocking dependencies found."
            }
          >
            <div className="cmts-kpi-strip">
              <MetricTile
                label="Blocking"
                value={impact.blockingCount}
                tone={impact.blockingCount > 0 ? "danger" : "success"}
              />
              <MetricTile
                label="Warnings"
                value={impact.warningCount}
                tone={impact.warningCount > 0 ? "warning" : "success"}
              />
            </div>
            <footer
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.75rem",
                marginTop: "1rem",
              }}
            >
              <Button
                label="Close"
                variant="secondary"
                onClick={() => setImpactOpen(false)}
              />
              {impactRow && (
                <Button
                  label="Deactivate Record"
                  variant="danger"
                  disabled={!impact.canProceed || busy}
                  onClick={() => handleDeactivate(impactRow)}
                />
              )}
            </footer>
          </Card>
        )}
      </CmtsEnterpriseModal>
    </PageContainer>
  );
}
